import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'next-i18next';
import { DEALERSHIP_AGE, JOB_FUNCTION } from './data';
import { FormControl, FormError, Input, InputMask, Select } from '@Backlot-Cars/archie';
import { useTreatments } from 'hooks/useTreatments';
import { JobFunctionDescriptionContainer, JobFunctionControls, Container } from './styles';
import RadioButtonFormField from './RadioButtonFormField';
import SignUpHeader from './header';
import SignUpFormActions from './FormActions';
import SignupResumeMessage from './ResumeMessage';
import { AddressAutocomplete } from 'components/AddressAutocomplete';
import STATES from 'helpers/states';

const StepOne = (props) => {
	const { t } = useTranslation(['common', 'signup']);
	const { onSubmit, initialValues, validationSchema } = props;
	const jobFunctionOther = 5;
	const jobFunctionValue = parseInt(initialValues.jobFunction);
	const [showJobDescription, setShowJobDescription] = useState(jobFunctionValue === jobFunctionOther);
	const {
		register,
		handleSubmit,
		setValue,
		formState: { errors, isSubmitting },
	} = useForm({
		defaultValues: {
			...initialValues,
		},
		resolver: yupResolver(validationSchema),
	});

	const treatmentsObj = useTreatments();
	const addressAutocompleteOn = treatmentsObj.address_autocomplete_signup === 'on';

	const handleJobFunctionChange = (value) => {
		setShowJobDescription(value === jobFunctionOther);
		setValue('jobFunction', value, { shouldValidate: true });
		if (value !== jobFunctionOther) {
			setValue('jobFunctionDescription', '');
		}
	};

	return (
		<>
			<SignupResumeMessage resumeStep={props.resumeStep} backButtonClicked={props.backButtonClicked} />
			<SignUpHeader title={t('signup:steps.step1.title')} />
			<FormControl label={t('signup:steps.step3.dealershipAge')} required formErrors={errors}>
				<RadioButtonFormField
					id="dealershipAge"
					options={DEALERSHIP_AGE}
					defaultValue={initialValues.dealershipAge}
					direction="row"
					itemPadding="0 30px 5px 0"
					itemAlignment="center"
					itemWrap={true}
					labelGap="8px"
					setValue={setValue}
					errors={errors}
				/>
			</FormControl>
			<JobFunctionControls>
				<FormControl label={t('signup:steps.step3.jobFunction')} required formErrors={errors}>
					<Select
						id="jobFunction"
						data-testid="jobFunction"
						placeholder={t('common:selectOne')}
						options={JOB_FUNCTION}
						defaultValue={parseInt(initialValues.jobFunction)}
						{...register('jobFunction', { onChange: (e) => handleJobFunctionChange(e.target.value) })}
					/>
				</FormControl>
				{showJobDescription && (
					<JobFunctionDescriptionContainer>
						<FormControl formErrors={errors}>
							<Input
								id="jobFunctionDescription"
								maxLength="255"
								placeholder="Enter your job title"
								{...register('jobFunctionDescription')}
							/>
						</FormControl>
					</JobFunctionDescriptionContainer>
				)}
			</JobFunctionControls>
			{addressAutocompleteOn ?
				<AddressAutocomplete
					register={register}
					errors={errors}
					setValue={setValue}
					label={t('signup:steps.step2.dealershipAddressAutocomplete')}
					placeholder={t('signup:steps.step2.dealershipAddressAutocomplete')}
				/>
			:	<>
					<FormControl label={t('signup:steps.step2.dealershipAddress')} required formErrors={errors}>
						<Input id="dealershipAddress" maxLength="255" {...register('dealershipAddress')} />
					</FormControl>
					<FormControl label={t('signup:steps.step2.dealershipAddress2')} formErrors={errors}>
						<Input id="dealershipAddress2" maxLength="255" {...register('dealershipAddress2')} />
					</FormControl>
					<Container>
						<FormControl label={t('signup:steps.step2.city')} required formErrors={errors}>
							<Input id="city" maxLength="255" {...register('city')} />
						</FormControl>
						<FormControl label={t('signup:steps.step2.state')} required formErrors={errors}>
							<Select
								id="state"
								maxItems="5"
								data-testid="state"
								options={Object.keys(STATES).map((key) => ({ value: key, text: key }))}
								{...register('state')}
							/>
						</FormControl>
						<FormControl label={t('signup:steps.step2.zip')} required formErrors={errors}>
							<Input id="zip" maxLength="5" {...register('zip')} />
						</FormControl>
					</Container>
				</>
			}
			<FormControl label={t('signup:steps.step1.dealershipPhone')} required formErrors={errors}>
				<InputMask
					id="dealershipPhone"
					prefix="+1"
					mask="(999) 999-9999"
					defaultValue={initialValues.dealershipPhone}
					{...register('dealershipPhone')}
				/>
			</FormControl>
			<FormError text={props.error} />
			<SignUpFormActions
				handleSubmit={handleSubmit((values) => onSubmit(values))}
				backCopy={t('signup:actions.back')}
				nextCopy={t('signup:actions.next')}
				isSubmitting={isSubmitting}
				{...props}
			/>
		</>
	);
};

export default StepOne;
